// supervisor-run-until-idle-tick-outcome.ts
//
// Split out of supervisor-run-until-idle.ts (phase 03 — trần dòng, mốc dự
// phòng ở Architecture khi file > 190): maps ONE `runOneSupervisorTick`
// result onto the loop's next action — a terminal `RunUntilIdleResult` to
// return, or "continue" to keep looping. Pure move of the tick-result
// if-chain (from `result.kind === "gate_blocked"` to the end of the loop
// body) — only textual change is wrapping the returns/continue in this
// function's own return shape.
import { readQuotaSleepState } from "./quota-sleep-scheduler.js";
import type { SchedulerProjection } from "./scheduler.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import type { SupervisorTickResult } from "./supervisor-loop-types.js";
import type { RunUntilIdleResult } from "./supervisor-run-until-idle-types.js";
import { explainStall } from "./ticket-dag-stall-explainer.js";

export type TickOutcomeAction =
  | { kind: "return"; result: RunUntilIdleResult }
  | { kind: "continue" };

export interface MapTickResultInput {
  result: SupervisorTickResult;
  tickets: Ticket[];
  projection: SchedulerProjection;
  tickIndex: number;
  g2UnenforcedConditions: readonly string[] | undefined;
  quotaStatePath: string;
  /** 06j (B8): lệnh gỡ `red_window_conflict` trong stallReason mang `--mission`. */
  missionId?: string;
}

/**
 * Re-review 2026-09-26 (A-c): what the loop reports when it falls off the end
 * of its tick budget. `idle` stays the outcome (JSON/e2e contract), but a DAG
 * with todo/in_progress tickets left is NOT idle — flag it and name the
 * tickets so printRunOutcome can tell the operator to run again.
 */
export function describeTickBudgetExhaustion(
  tickets: Ticket[],
  ticketStatus: Record<string, string>,
): Pick<RunUntilIdleResult, "tickBudgetExhausted" | "pendingTicketIds"> {
  const pending = tickets
    .filter((t) => ["todo", "in_progress"].includes(ticketStatus[t.id] ?? "todo"))
    .map((t) => t.id);
  return pending.length === 0 ? {} : { tickBudgetExhausted: true, pendingTicketIds: pending };
}

/** Maps `runOneSupervisorTick`'s result to the next loop action. `undefined` when the result needs no terminal handling here (the loop moves on to the next iteration without an explicit `continue` marker — currently unreachable, kept for exhaustiveness). */
export async function mapTickResultToLoopAction(
  input: MapTickResultInput,
): Promise<TickOutcomeAction> {
  const {
    result,
    tickets,
    projection,
    tickIndex: i,
    g2UnenforcedConditions,
    quotaStatePath,
    missionId,
  } = input;

  if (result.kind === "gate_blocked") {
    // Bug I (P15): a feature ticket is ready but G0/G1 authority is not
    // granted. Terminal for this `run`, like a cost/attempt ceiling and
    // unlike quiet_hours — sleeping would just spin to maxTicks, since
    // only a HUMAN approving the gate (a separate process writing
    // gates.json) can change this. The operator sees why via the returned
    // reason; a later `run` after `approve g0/g1` picks up where it left
    // off, because nothing was mutated.
    return {
      kind: "return",
      result: {
        outcome: "blocked",
        ticksRun: i + 1,
        gateBlocked: {
          ticketId: result.ticketId,
          ticketKind: result.ticketKind,
          code: result.code,
          reason: result.reason,
        },
        g2UnenforcedConditions,
      },
    };
  }
  if (result.kind === "idle") {
    // Nothing ready and not all done => either blocked tickets stalled
    // everything, or the DAG is genuinely empty.
    const stalled = tickets.length > 0;
    // Req 3 (R-15): human-readable reasons for the operator — empty when
    // explainStall finds nothing (e.g. a busy in_progress ticket, not a
    // real DAG stall), so the field stays absent rather than `[]` noise.
    const reasons = stalled ? explainStall(tickets, projection.ticketStatus, missionId) : [];
    return {
      kind: "return",
      result: {
        outcome: stalled ? "blocked" : "idle",
        ticksRun: i + 1,
        stallReason: reasons.length > 0 ? reasons : undefined,
        g2UnenforcedConditions,
      },
    };
  }
  if (result.route.kind === "quota_sleep") {
    return {
      kind: "return",
      result: {
        outcome: "quota_sleeping",
        ticksRun: i + 1,
        wakeAt: (await readQuotaSleepState(quotaStatePath))?.wake_at ?? result.route.wakeAt,
        g2UnenforcedConditions,
      },
    };
  }
  if (result.route.kind === "retry_decision" && result.route.decision.outcome === "blocked") {
    // A blocked ticket doesn't halt independent tickets (test 8) — keep
    // looping; the next `nextReadyTicket` call skips it naturally since
    // its status is no longer "todo".
    return { kind: "continue" };
  }
  if (
    result.route.kind === "retry_decision" &&
    result.route.decision.outcome === "escalate_waiting_human"
  ) {
    return {
      kind: "return",
      result: { outcome: "blocked", ticksRun: i + 1, g2UnenforcedConditions },
    };
  }
  return { kind: "continue" };
}
